import type { Address } from "viem";
import { zeroAddress } from "viem";

/**
 * Runtime configuration for the dApp.
 *
 * Contract addresses come from the deployment (see docs/deployment.md); until the network is
 * deployed they are the zero address and the UI shows a "not configured" banner. Override any value
 * at build time with a VITE_* env var (see .env.example), or at runtime by defining
 * `window.__BASEDAI_CONFIG__` before the app bundle loads (lets an operator point a prebuilt bundle
 * at fresh addresses without a rebuild).
 */

export interface ChainContracts {
  based: Address;
  subnetRegistry: Address;
  market: Address;
  stakingVault: Address;
  scoringRegistry: Address;
  brainNFTL2: Address;
  governor: Address;
  timelock: Address;
}

export interface L1Contracts {
  brainNFT: Address;
  bridgeAdapter: Address;
  l1Erc721Bridge: Address;
  pepecoin: Address;
  basedAI: Address;
}

export interface NetworkDef {
  chainId: number;
  name: string;
  rpcUrl: string;
  explorer: string;
  nativeSymbol: string;
}

export interface AppConfig {
  /** The L2 (Ink) where the market, staking, scoring, governance live — the dApp's home chain. */
  l2: NetworkDef & {
    contracts: ChainContracts;
    /** Block the core L2 contracts were deployed at — the floor for event scans. 0 = unset (scan
     *  from genesis; fine on a fresh/preview chain, but public RPCs cap getLogs ranges on a mature
     *  one, so set VITE_L2_DEPLOYMENT_BLOCK once contracts are live). */
    deploymentBlock: bigint;
  };
  /** The L1 (Ethereum) where the Brain NFT is minted by staking Pepecoin / $basedAI. */
  l1: NetworkDef & { contracts: L1Contracts };
  /** HTTP gateway for miner discovery + inference proxy. */
  gatewayUrl: string;
  /** External governance UI (BasedGovernor is a stock OZ Governor — Tally supports it directly). */
  governanceUrl: string;
  /** Ink's canonical bridge UI for moving Brains L1 <-> L2. */
  bridgeUrl: string;
}

type RuntimeOverride = { __BASEDAI_CONFIG__?: DeepPartial<AppConfig> };
type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

const env = import.meta.env;
const addr = (v: string | undefined, fallback: Address = zeroAddress): Address =>
  (v && /^0x[0-9a-fA-F]{40}$/.test(v) ? (v as Address) : fallback);

// Ink Sepolia testnet defaults (docs/deployment.md). Ink canonical L2ERC721Bridge predeploy is fixed.
const DEFAULTS: AppConfig = {
  l2: {
    chainId: Number(env.VITE_L2_CHAIN_ID ?? 763373),
    name: env.VITE_L2_NAME ?? "Ink Sepolia",
    rpcUrl: env.VITE_L2_RPC ?? "https://rpc-gel-sepolia.inkonchain.com",
    explorer: env.VITE_L2_EXPLORER ?? "https://explorer-sepolia.inkonchain.com",
    nativeSymbol: "ETH",
    deploymentBlock: BigInt(env.VITE_L2_DEPLOYMENT_BLOCK ?? 0),
    contracts: {
      based: addr(env.VITE_L2_BASED),
      subnetRegistry: addr(env.VITE_SUBNET_REGISTRY),
      market: addr(env.VITE_MARKET),
      stakingVault: addr(env.VITE_STAKING_VAULT),
      scoringRegistry: addr(env.VITE_SCORING_REGISTRY),
      brainNFTL2: addr(env.VITE_BRAIN_NFT_L2),
      governor: addr(env.VITE_GOVERNOR),
      timelock: addr(env.VITE_TIMELOCK),
    },
  },
  l1: {
    chainId: Number(env.VITE_L1_CHAIN_ID ?? 11155111),
    name: env.VITE_L1_NAME ?? "Ethereum Sepolia",
    rpcUrl: env.VITE_L1_RPC ?? "https://ethereum-sepolia-rpc.publicnode.com",
    explorer: env.VITE_L1_EXPLORER ?? "https://sepolia.etherscan.io",
    nativeSymbol: "ETH",
    contracts: {
      brainNFT: addr(env.VITE_L1_BRAIN_NFT),
      bridgeAdapter: addr(env.VITE_L1_BRIDGE_ADAPTER),
      l1Erc721Bridge: addr(env.VITE_L1_ERC721_BRIDGE, "0xd1c901bbd7796546a7ba2492e0e199911fae68c7"),
      pepecoin: addr(env.VITE_L1_PEPECOIN),
      basedAI: addr(env.VITE_L1_BASEDAI),
    },
  },
  gatewayUrl: env.VITE_GATEWAY_URL ?? "http://localhost:8080",
  governanceUrl: env.VITE_GOVERNANCE_URL ?? "https://www.tally.xyz",
  bridgeUrl: env.VITE_BRIDGE_URL ?? "https://inkonchain.com/bridge",
};

function merge(base: AppConfig, over?: DeepPartial<AppConfig>): AppConfig {
  if (!over) return base;
  return {
    ...base,
    ...over,
    l2: { ...base.l2, ...over.l2, contracts: { ...base.l2.contracts, ...over.l2?.contracts } },
    l1: { ...base.l1, ...over.l1, contracts: { ...base.l1.contracts, ...over.l1?.contracts } },
  };
}

export const config: AppConfig = merge(
  DEFAULTS,
  (globalThis as unknown as RuntimeOverride).__BASEDAI_CONFIG__,
);

/** True if every core L2 contract address is set — otherwise the app runs in "preview" mode. */
export function isConfigured(): boolean {
  const c = config.l2.contracts;
  return [c.based, c.subnetRegistry, c.market, c.stakingVault].every((a) => a !== zeroAddress);
}

export const BRAIN_SUPPLY_CAP = 64;
